import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Users, Receipt, Gift, Check, X, Wallet as WalletIcon, Save, FolderTree, ListChecks, Activity, ScrollText, SlidersHorizontal, Eye, ShieldCheck, Coins, Megaphone, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { luxe } from '@/lib/luxe';
import { GlassCard, LuxeButton, SectionTitle, StatCard, Spinner, Empty, Input, Field } from '@/components/bits';
import { ManageTabs } from '@/components/ManagePanels';

const TABS = ['Dashboard', 'Clients', 'Projects', 'Coding', 'Manage', 'Transactions', 'Offers', 'Security', 'Audit', 'Health', 'Settings'];
const SEV_TONE = { CRITICAL: '#F87171', HIGH: '#FCA5A5', MEDIUM: luxe.warning, LOW: luxe.blue, INFO: luxe.muted };

const HEALTH_TONE = { healthy: luxe.success, configured: luxe.success, degraded: luxe.warning, not_configured: luxe.muted, unavailable: luxe.danger, infrastructure_dependency: luxe.warning };

export default function Admin() {
  const [tab, setTab] = useState('Dashboard');
  const [kpi, setKpi] = useState({});
  const [signups, setSignups] = useState([]);
  const [users, setUsers] = useState([]);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [giftFor, setGiftFor] = useState(null);
  const [giftAmt, setGiftAmt] = useState(50);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [overview, setOverview] = useState(null);
  const [projects, setProjects] = useState([]);
  const [codingTasks, setCodingTasks] = useState([]);
  const [audit, setAudit] = useState([]);
  const [health, setHealth] = useState(null);
  const [security, setSecurity] = useState(null);
  const [clientView, setClientView] = useState(null);
  const [offers, setOffers] = useState([]);
  const [offerForm, setOfferForm] = useState({ title: '', message: '', kind: 'info' });

  const loadOffers = () => api('/studio-admin/broadcasts').then((r) => setOffers(r.items || [])).catch(() => {});
  const createOffer = async () => {
    if (!offerForm.title.trim() || !offerForm.message.trim()) return toast.error('Title aur message dono likhein');
    try { await api('/studio-admin/broadcast', { method: 'POST', body: offerForm }); toast.success('Offer live — ab users ko dikhega'); setOfferForm({ title: '', message: '', kind: 'info' }); loadOffers(); }
    catch (e) { toast.error(e.message); }
  };
  const delOffer = async (id) => { try { await api(`/studio-admin/broadcasts/${id}`, { method: 'DELETE' }); setOffers((o) => o.filter((x) => x.id !== id)); } catch (e) { toast.error(e.message); } };

  const openClient = async (id) => {
    try { setClientView(await api(`/admin/console/client/${id}`)); }
    catch (e) { toast.error(e.message); }
  };

  useEffect(() => {
    if (tab === 'Dashboard' && !overview) api('/admin/console/overview').then(setOverview).catch(() => {});
    if (tab === 'Projects') api('/studio-admin/projects').then((r) => setProjects(r.items || r || [])).catch(() => {});
    if (tab === 'Coding') api('/admin/coding/queue').then((r) => setCodingTasks(r.tasks || [])).catch(() => {});
    if (tab === 'Audit') api('/admin/console/audit').then((r) => setAudit(r.events || [])).catch(() => {});
    if (tab === 'Security') api('/admin/console/security').then(setSecurity).catch(() => {});
    if (tab === 'Health') api('/admin/console/health').then((r) => setHealth(r.checks || {})).catch(() => {});
    if (tab === 'Offers') loadOffers();
  }, [tab]); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    try {
      const [d, u, t, s] = await Promise.all([
        api('/studio-admin/dashboard'),
        api('/studio-admin/users').catch(() => ({ items: [] })),
        api('/studio-admin/txns').catch(() => ({ items: [] })),
        api('/studio-admin/settings').catch(() => null),
      ]);
      setKpi(d.kpi); setSignups(d.recent_signups || []);
      setUsers(u.items || []); setTxns(t.items || []);
      if (s) setSettings({ wallets: s.wallets || {}, signup_bonus: s.signup_bonus ?? 20, credits_enabled: !!s.credits_enabled });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api('/studio-admin/settings', { method: 'POST', body: { wallets: settings.wallets, signup_bonus: +settings.signup_bonus } });
      toast.success('Wallet settings saved — payments are now live');
    } catch (e) { toast.error(e.message); } finally { setSavingSettings(false); }
  };

  const toggleCredits = async () => {
    const next = !settings.credits_enabled;
    try {
      await api('/studio-admin/settings', { method: 'POST', body: { credits_enabled: next } });
      setSettings((s) => ({ ...s, credits_enabled: next }));
      toast.success(next ? 'Credit purchases turned ON for users' : 'Credit purchases turned OFF');
    } catch (e) { toast.error(e.message); }
  };

  const gift = async () => {
    try {
      await api(`/studio-admin/users/${giftFor.id}/gift`, { method: 'POST', body: { amount: +giftAmt } });
      toast.success(`Gifted ${giftAmt} credits`); setGiftFor(null); load();
    } catch (e) { toast.error(e.message); }
  };
  const approve = async (id) => { try { await api(`/studio-admin/txns/${id}/approve`, { method: 'POST' }); toast.success('Approved'); load(); } catch (e) { toast.error(e.message); } };
  const reject = async (id) => { try { await api(`/studio-admin/txns/${id}/reject`, { method: 'POST', body: { reason: 'Not verified on chain' } }); toast.success('Rejected'); load(); } catch (e) { toast.error(e.message); } };

  return (
    <div>
      <SectionTitle eyebrow="Studio Admin" title="Admin Console" color={luxe.blue}
        desc="Manage creators, verify crypto payments, and monitor your studio in real time." />

      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} className="px-4 py-2 rounded-full text-sm font-bold luxe-btn"
            style={tab === t ? { background: 'linear-gradient(135deg,#3B82F6,#1D4ED8)', color: '#fff' } : { background: luxe.surface, color: luxe.textMid, border: `1px solid ${luxe.border}` }}>{t}</button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-16"><Spinner size={28} /></div> : (
        <>
          {tab === 'Dashboard' && (
            <div className="float-in">
              {overview && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4" data-testid="admin-overview">
                  <StatCard label="Tenants" value={overview.tenants} accent={luxe.blue} />
                  <StatCard label="Projects" value={overview.projects} accent={luxe.text} />
                  <StatCard label="Coding Tasks" value={overview.coding_tasks?.total} accent={luxe.gold} />
                  <StatCard label="Credits Outstanding" value={overview.credits_outstanding} accent={luxe.success} />
                  <StatCard label="Queued" value={overview.coding_tasks?.QUEUED} accent={luxe.muted} />
                  <StatCard label="Running" value={overview.coding_tasks?.RUNNING} accent={luxe.blue} />
                  <StatCard label="Failed" value={overview.coding_tasks?.FAILED} accent={luxe.danger} />
                  <StatCard label="AI Providers" value={`${overview.ai_providers?.configured}/${overview.ai_providers?.total}`} accent={luxe.success} />
                </div>
              )}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard label="Total Creators" value={kpi.total_users || 0} accent={luxe.blue} />
                <StatCard label="Projects" value={kpi.total_projects || 0} accent={luxe.text} />
                <StatCard label="Pending Payments" value={kpi.pending_txns || 0} accent={luxe.warning} />
                <StatCard label="Revenue" value={`$${kpi.total_revenue_usd || 0}`} accent={luxe.success} />
              </div>
              <div className="text-sm font-bold mb-3" style={{ color: luxe.text }}>Recent Signups</div>
              {signups.length === 0 ? <Empty icon={Users} title="No signups yet" /> : (
                <div className="space-y-2">
                  {signups.map((s) => (
                    <GlassCard key={s.id} className="p-4 flex items-center justify-between">
                      <div><div className="font-bold" style={{ color: luxe.text }}>{s.name}</div><div className="text-xs" style={{ color: luxe.muted }}>{s.email}</div></div>
                      <div className="text-sm font-bold gold-text">{s.credits_balance || 0} cr</div>
                    </GlassCard>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'Clients' && (
            <div className="float-in space-y-2" data-testid="admin-clients">
              {users.length === 0 ? <Empty icon={Users} title="No users" /> : users.map((u) => (
                <GlassCard key={u.id} className="p-4 flex items-center justify-between">
                  <div><div className="font-bold" style={{ color: luxe.text }}>{u.name}</div><div className="text-xs" style={{ color: luxe.muted }}>{u.email} · {u.credits_balance || 0} credits</div></div>
                  <div className="flex gap-2">
                    <LuxeButton variant="ghost" onClick={() => openClient(u.id)} data-testid={`client-view-${u.id}`}><Eye size={15} className="inline mr-1" />View</LuxeButton>
                    <LuxeButton variant="ghost" onClick={() => { setGiftFor(u); setGiftAmt(50); }}><Gift size={15} className="inline mr-1" />Gift</LuxeButton>
                  </div>
                </GlassCard>
              ))}
            </div>
          )}

          {tab === 'Projects' && (
            <div className="float-in space-y-2" data-testid="admin-projects">
              {projects.length === 0 ? <Empty icon={FolderTree} title="No projects" /> : projects.map((p) => (
                <GlassCard key={p.id} className="p-4 flex items-center justify-between">
                  <div><div className="font-bold" style={{ color: luxe.text }}>{p.name}</div><div className="text-xs" style={{ color: luxe.muted }}>{p.id} · owner {p.added_by || '—'} {p.forked_from ? `· forked from ${p.forked_from}` : ''}</div></div>
                  <span className="text-[11px]" style={{ color: luxe.muted }}>{(p.created_at || '').slice(0, 16)}</span>
                </GlassCard>
              ))}
            </div>
          )}

          {tab === 'Coding' && (
            <div className="float-in space-y-2" data-testid="admin-coding">
              {codingTasks.length === 0 ? <Empty icon={ListChecks} title="No coding tasks" desc="Queue is empty across tenants." /> : codingTasks.map((t) => (
                <GlassCard key={t.id} className="p-4 flex items-center justify-between flex-wrap gap-2">
                  <div><div className="font-bold text-sm" style={{ color: luxe.text }}>{t.current_step || t.assigned_agent}</div><div className="text-xs" style={{ color: luxe.muted }}>{t.project} · {t.assigned_agent} · attempt {t.attempt_count}/{t.max_attempts} · owner {t.owner}</div></div>
                  <span className="text-xs font-bold" style={{ color: HEALTH_TONE[String(t.status).toLowerCase()] || luxe.textMid }}>{t.status}</span>
                </GlassCard>
              ))}
            </div>
          )}

          {tab === 'Manage' && (
            <div className="float-in" data-testid="admin-manage"><ManageTabs initial="connectors" /></div>
          )}

          {tab === 'Offers' && (
            <div className="float-in space-y-4" data-testid="admin-offers">
              <GlassCard className="p-6 max-w-2xl">
                <div className="flex items-center gap-2 mb-1"><Megaphone size={18} style={{ color: luxe.blue }} /><span className="font-bold" style={{ color: luxe.text }}>Create an Offer / Announcement</span></div>
                <p className="text-sm mb-4" style={{ color: luxe.muted }}>Yeh har user ko unke dashboard par banner ki tarah dikhega. (Admin ko nahi dikhta.)</p>
                <Field label="Title"><Input value={offerForm.title} onChange={(e) => setOfferForm({ ...offerForm, title: e.target.value })} placeholder="e.g. Diwali Offer — 20% extra credits" data-testid="offer-title" /></Field>
                <Field label="Message"><Input value={offerForm.message} onChange={(e) => setOfferForm({ ...offerForm, message: e.target.value })} placeholder="Short message users ko dikhane ke liye" data-testid="offer-message" /></Field>
                <Field label="Type">
                  <select value={offerForm.kind} onChange={(e) => setOfferForm({ ...offerForm, kind: e.target.value })} data-testid="offer-kind" className="w-full outline-none" style={{ padding: '10px 12px', borderRadius: 12, border: `1px solid ${luxe.border}`, background: luxe.surface, color: luxe.text }}>
                    <option value="info">Info (blue)</option>
                    <option value="success">Success (green)</option>
                    <option value="warning">Warning (amber)</option>
                  </select>
                </Field>
                <LuxeButton onClick={createOffer} data-testid="offer-create"><Megaphone size={15} className="inline mr-1" />Publish Offer</LuxeButton>
              </GlassCard>
              <div className="space-y-2">
                {offers.length === 0 ? <Empty icon={Megaphone} title="No offers yet" desc="Create one above to show it to all users." /> : offers.map((o) => (
                  <GlassCard key={o.id} className="p-4 flex items-center justify-between gap-2">
                    <div><div className="font-bold text-sm" style={{ color: luxe.text }}>{o.title} <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ml-1" style={{ background: luxe.surface2, color: luxe.muted }}>{o.kind}</span></div><div className="text-xs" style={{ color: luxe.muted }}>{o.message}</div></div>
                    <button onClick={() => delOffer(o.id)} data-testid={`offer-del-${o.id}`} className="p-2 rounded-lg" style={{ background: 'rgba(228,72,60,0.1)', color: luxe.danger }}><Trash2 size={14} /></button>
                  </GlassCard>
                ))}
              </div>
            </div>
          )}

          {tab === 'Security' && (
            <div className="float-in" data-testid="admin-security">
              {!security ? <Spinner /> : (
                <>
                  <div className="flex gap-2 mb-3 flex-wrap text-xs">
                    {Object.entries(security.by_severity || {}).map(([s, n]) => (
                      <span key={s} className="px-2 py-1 rounded-full font-bold" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}`, color: SEV_TONE[s] || luxe.muted }}>{s}: {n}</span>
                    ))}
                  </div>
                  {(security.events || []).length === 0 ? <Empty icon={ShieldCheck} title="No security signals" desc="Rule-based signals appear here from the audit trail." />
                    : (security.events || []).map((e, i) => (
                      <div key={i} className="p-2.5 rounded-lg mb-1 flex items-center justify-between text-xs" style={{ background: luxe.surface, border: `1px solid ${luxe.border}` }}>
                        <div><b style={{ color: SEV_TONE[e.severity] || luxe.text }}>{e.severity}</b> · <span style={{ color: luxe.text }}>{e.type}</span> <span style={{ color: luxe.muted }}>· {e.kind} · actor {e.actor || '—'}{e.tenant ? ` · ${e.tenant}` : ''}</span></div>
                        <span style={{ color: luxe.muted }}>{(e.at || '').slice(0, 19)}</span>
                      </div>
                    ))}
                  <div className="text-[11px] mt-2" style={{ color: luxe.muted }}>Rule-based security signals (explainable) — no fabricated AI incidents. No secrets shown.</div>
                </>
              )}
            </div>
          )}
          {tab === 'Audit' && (
            <div className="float-in space-y-1" data-testid="admin-audit">
              {audit.length === 0 ? <Empty icon={ScrollText} title="No audit events" /> : audit.map((a, i) => (
                <div key={i} className="p-2.5 rounded-lg text-xs font-mono" style={{ background: luxe.surface, border: `1px solid ${luxe.border}`, color: luxe.textMid }}>
                  {(a.at || '').slice(0, 19)} · <b style={{ color: luxe.text }}>{a.kind}/{a.action}</b> · actor {a.actor || '—'} {a.project || a.new_project ? `· ${a.project || a.new_project}` : ''} {a.provider ? `· ${a.provider}` : ''}
                </div>
              ))}
            </div>
          )}

          {tab === 'Health' && (
            <div className="float-in" data-testid="admin-health">
              {!health ? <Spinner /> : (
                <div className="grid sm:grid-cols-2 gap-2">
                  {Object.entries(health).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between p-3 rounded-lg" style={{ background: luxe.surface, border: `1px solid ${luxe.border}` }}>
                      <span className="text-sm capitalize" style={{ color: luxe.text }}>{k.replace(/_/g, ' ')}</span>
                      <span className="text-xs font-bold capitalize" style={{ color: HEALTH_TONE[v] || luxe.muted }}>{String(v).replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'Transactions' && (
            <div className="float-in space-y-2">
              {txns.length === 0 ? <Empty icon={Receipt} title="No transactions" /> : txns.map((t) => (
                <GlassCard key={t.id} className="p-4 flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-bold text-sm capitalize" style={{ color: luxe.text }}>{(t.type || '').replace('_', ' ')} · {t.user_email}</div>
                    <div className="text-xs" style={{ color: luxe.muted }}>{t.status} · {(t.meta?.package_name) || ''} {t.meta?.currency ? `· ${t.meta.currency}` : ''}</div>
                  </div>
                  {t.status === 'pending' ? (
                    <div className="flex gap-2">
                      <button onClick={() => approve(t.id)} className="px-3 py-1.5 rounded-lg text-xs font-bold" style={{ background: 'rgba(14,169,114,0.12)', color: luxe.success, border: '1px solid rgba(14,169,114,0.3)' }}><Check size={13} className="inline" /> Approve</button>
                      <button onClick={() => reject(t.id)} className="px-3 py-1.5 rounded-lg text-xs font-bold" style={{ background: 'rgba(228,72,60,0.1)', color: luxe.danger, border: '1px solid rgba(228,72,60,0.3)' }}><X size={13} className="inline" /> Reject</button>
                    </div>
                  ) : <span className="text-sm font-bold capitalize" style={{ color: t.status === 'confirmed' ? luxe.success : luxe.muted }}>{t.status}</span>}
                </GlassCard>
              ))}
            </div>
          )}
          {tab === 'Settings' && (
            <div className="float-in space-y-4">
              <GlassCard className="p-6 max-w-2xl">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 mb-1"><Coins size={18} style={{ color: luxe.gold }} /><span className="font-bold" style={{ color: luxe.text }}>Credit Purchases</span></div>
                    <p className="text-sm" style={{ color: luxe.muted }}>
                      Users ke liye credit buy option ko ON/OFF karein. {settings?.credits_enabled
                        ? 'Abhi users credits khareed sakte hain.'
                        : 'Abhi buy option band hai — users ko purchase screen nahi dikhega.'}
                    </p>
                  </div>
                  <button
                    onClick={toggleCredits}
                    data-testid="toggle-credits-btn"
                    className="relative w-16 h-9 rounded-full transition-all flex-shrink-0"
                    style={{ background: settings?.credits_enabled ? luxe.success : luxe.surface2, border: `1px solid ${settings?.credits_enabled ? 'rgba(14,169,114,0.5)' : luxe.border}` }}>
                    <span className="absolute top-1 w-7 h-7 rounded-full transition-all" style={{ left: settings?.credits_enabled ? 32 : 4, background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }} />
                  </button>
                </div>
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold" style={{ background: settings?.credits_enabled ? 'rgba(14,169,114,0.12)' : 'rgba(138,145,157,0.12)', color: settings?.credits_enabled ? luxe.success : luxe.muted }} data-testid="credits-status">
                  {settings?.credits_enabled ? '● Credit buying is ON' : '○ Credit buying is OFF'}
                </div>
              </GlassCard>

              <GlassCard className="p-6 max-w-2xl">
                <div className="flex items-center gap-2 mb-1"><WalletIcon size={18} style={{ color: luxe.gold }} /><span className="font-bold" style={{ color: luxe.text }}>Crypto Wallet Addresses</span></div>
                <p className="text-sm mb-5" style={{ color: luxe.muted }}>Set your real receiving addresses. These show to users on the payment screen — so top-ups reach you.</p>
                {!settings ? <Empty icon={WalletIcon} title="Could not load settings" /> : (
                  <>
                    {Object.keys(settings.wallets).map((cur) => (
                      <Field key={cur} label={`${cur} Address`}>
                        <Input value={settings.wallets[cur] || ''} onChange={(e) => setSettings({ ...settings, wallets: { ...settings.wallets, [cur]: e.target.value } })} placeholder={`Your ${cur} wallet address`} data-testid={`wallet-${cur}`} />
                      </Field>
                    ))}
                    <Field label="Signup Bonus (credits)">
                      <Input type="number" value={settings.signup_bonus} onChange={(e) => setSettings({ ...settings, signup_bonus: e.target.value })} />
                    </Field>
                    <LuxeButton onClick={saveSettings} disabled={savingSettings} data-testid="save-settings">
                      {savingSettings ? <span className="flex gap-2 items-center"><Spinner color="#3A2C05" /> Saving…</span> : <><Save size={16} className="inline mr-1" />Save Settings</>}
                    </LuxeButton>
                  </>
                )}
              </GlassCard>
            </div>
          )}
        </>
      )}
      {clientView && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="admin-client-drawer" onClick={() => setClientView(null)}>
          <div className="flex-1" style={{ background: 'rgba(0,0,0,0.6)' }} />
          <div className="w-full max-w-xl h-full overflow-y-auto p-6" style={{ background: luxe.bgElev, borderLeft: `1px solid ${luxe.border}` }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="font-display text-xl font-black" style={{ color: luxe.text }}>{clientView.profile?.name}</div>
                <div className="text-xs" style={{ color: luxe.muted }}>{clientView.profile?.email} · {clientView.profile?.role} · {clientView.credits?.balance} credits</div>
              </div>
              <button onClick={() => setClientView(null)} data-testid="admin-client-close" className="p-2 rounded-lg" style={{ background: luxe.surface, border: `1px solid ${luxe.border}`, color: luxe.textMid }}><X size={16} /></button>
            </div>
            {[['Projects', clientView.projects, (p) => `${p.name} · ${p.id}`],
              ['Coding tasks', clientView.coding_tasks, (t) => `${t.current_step || t.assigned_agent} · ${t.status}`],
              ['Connectors', clientView.connectors, (c) => `${c.platform} · ${c.status || 'connected'}`]].map(([label, arr, fmt]) => (
              <div key={label} className="mb-4">
                <div className="text-[10px] font-extrabold uppercase tracking-widest mb-1" style={{ color: luxe.muted }}>{label} ({(arr || []).length})</div>
                {(arr || []).length === 0 ? <div className="text-xs" style={{ color: luxe.muted }}>None</div>
                  : (arr || []).slice(0, 20).map((x, i) => <div key={i} className="text-xs py-1" style={{ color: luxe.textMid }}>{fmt(x)}</div>)}
              </div>
            ))}
            <div className="mb-2">
              <div className="text-[10px] font-extrabold uppercase tracking-widest mb-1" style={{ color: luxe.muted }}>Credits by type</div>
              {Object.entries(clientView.credits?.by_type || {}).map(([k, v]) => <div key={k} className="text-xs" style={{ color: luxe.textMid }}>{k}: {v}</div>)}
              <div className="text-xs mt-2" style={{ color: luxe.muted }}>Plan {clientView.billing?.plan} · payment provider {clientView.billing?.payment_provider_configured ? 'configured' : 'not configured'}</div>
            </div>
            <div className="text-[11px] mt-3" style={{ color: luxe.muted }}>Operational visibility only — no passwords, PATs, API keys or decrypted credentials are shown.</div>
          </div>
        </div>
      )}
      {giftFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(11,21,51,0.5)' }} onClick={() => setGiftFor(null)}>
          <GlassCard className="w-full max-w-sm p-6" style={{ background: '#12142B' }} onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-xl font-black mb-1" style={{ color: luxe.text }}>Gift Credits</div>
            <div className="text-sm mb-4" style={{ color: luxe.muted }}>to {giftFor.name}</div>
            <Input type="number" value={giftAmt} onChange={(e) => setGiftAmt(e.target.value)} />
            <div className="flex gap-2 mt-4">
              <LuxeButton full onClick={gift}>Gift {giftAmt} credits</LuxeButton>
              <LuxeButton variant="ghost" onClick={() => setGiftFor(null)}>Cancel</LuxeButton>
            </div>
          </GlassCard>
        </div>
      )}
    </div>
  );
}
